import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';

import { relativeUrlResolver } from '@/services/utils';

const USER_PREFERENCES_URL = '/api/v1/user/preferences';

/** Shared with `useGridPreferences`, so both read and update the same cache entry. */
const USER_PREFERENCES_QUERY_KEY = ['user', 'preferences'] as const;

/** Fields picked in the library menu "Card view" (D-036). */
const DAM_CARD_VIEW_PREFERENCE = 'damCardView';

/** Grid or list view of the library (D-036). */
const DAM_LIBRARY_VIEW_PREFERENCE = 'damLibraryView';

/**
 * One custom user preference (`/api/v1/user/preferences/{key}`). Values are stored as
 * objects; `undefined` while loading or when the user never saved the key.
 */
function useDamPreference<T extends object>(key: string) {
    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: USER_PREFERENCES_QUERY_KEY,
        queryFn: async () => (await axios.get<Record<string, unknown>>(relativeUrlResolver(USER_PREFERENCES_URL))).data ?? {},
    });

    /** Updates the cache optimistically; on failure the cache is reloaded from the server and the error rethrown. */
    const save = useCallback(async (value: T) => {
        queryClient.setQueryData<Record<string, unknown>>(USER_PREFERENCES_QUERY_KEY, (previous) => ({ ...(previous ?? {}), [key]: value }));
        try {
            await axios.put(relativeUrlResolver(`${USER_PREFERENCES_URL}/${encodeURIComponent(key)}`), value);
        } catch (error) {
            void queryClient.invalidateQueries({ queryKey: USER_PREFERENCES_QUERY_KEY });
            throw error;
        }
    }, [key, queryClient]);

    const value = data?.[key];
    return {
        value: value !== null && typeof value === 'object' ? value as Partial<T> : undefined,
        isLoading,
        save,
    };
}

export { DAM_CARD_VIEW_PREFERENCE, DAM_LIBRARY_VIEW_PREFERENCE, useDamPreference };
